/**
 * @fileoverview Route guard reproducing the Angular `loginGuard`.
 * @module app/guards/RedirectIfAuthed
 * @description
 * React port of `loginGuard` (`angular/src/app/guards/auth.guard.ts`). Prevents
 * already-authenticated users from reaching the login/registration screens.
 * Reproduces the Angular logic exactly:
 *
 *   - If `data.isLoading` (session init) -> allow (render children). Angular
 *     returned `true` during initialization to permit the login screen.
 *   - Else if `isLoggedIn` -> redirect to `/<getNextStep()>`.
 *   - Else -> render children.
 *
 * @author Liraz Amir
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDataStore } from '@/stores/data.store';
import { useAuthStore } from '@/stores/auth.store';

export function RedirectIfAuthed() {
  const dataLoading = useDataStore((s) => s.isLoading);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);

  // During session init, allow access to the public auth screens.
  if (dataLoading) {
    return <Outlet />;
  }

  // Already authenticated -> send to the user's next step.
  if (isLoggedIn) {
    const nextStep = useAuthStore.getState().getNextStep();
    return <Navigate to={`/${nextStep}`} replace />;
  }

  return <Outlet />;
}

export default RedirectIfAuthed;
